/**
 * User management and invite API methods.
 */

import apiClient from './client';
import type { Role } from '@/types/auth';
import type { Invitation, InviteRequest, AcceptInviteRequest, InviteTokenInfo } from '@/types/invitation';

export interface UserCreateRequest {
  email: string;
  password: string;
  full_name: string;
  role: Role;
}

export interface UserRecord {
  id: string;
  email: string;
  full_name: string;
  role: Role;
  tenant_id: string;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

/** List all users in the caller's tenant. */
export async function listUsers(): Promise<UserRecord[]> {
  const { data } = await apiClient.get<UserRecord[]>('/v1/users');
  return data;
}

/** Create a new user in the caller's tenant (quota enforced by API). */
export async function createUser(payload: UserCreateRequest): Promise<UserRecord> {
  const { data } = await apiClient.post<UserRecord>('/v1/users', payload);
  return data;
}

/** Deactivate (soft-delete) a user. */
export async function deactivateUser(userId: string): Promise<void> {
  await apiClient.delete(`/v1/users/${userId}`);
}

/** Send an invite email to a new user (quota enforced). */
export async function sendInvite(payload: InviteRequest): Promise<Invitation> {
  const { data } = await apiClient.post<Invitation>('/v1/users/invite', payload);
  return data;
}

/** List all pending invitations for the caller's tenant. */
export async function listInvitations(): Promise<Invitation[]> {
  const { data } = await apiClient.get<Invitation[]>('/v1/users/invitations');
  return data;
}

/** Revoke (delete) a pending invitation. */
export async function revokeInvitation(id: string): Promise<void> {
  await apiClient.delete(`/v1/users/invitations/${id}`);
}

/** Resend (regenerate token) an invitation. */
export async function resendInvitation(id: string): Promise<Invitation> {
  const { data } = await apiClient.post<Invitation>(`/v1/users/invitations/${id}/resend`);
  return data;
}

/** Validate an invite token and get metadata (public, no auth required). */
export async function validateInviteToken(token: string): Promise<InviteTokenInfo> {
  const { data } = await apiClient.get<InviteTokenInfo>(`/v1/auth/invite/${token}`);
  return data;
}

/** Accept an invite and create an account (public, no auth required). */
export async function acceptInvite(token: string, payload: AcceptInviteRequest): Promise<import('@/types/auth').LoginResponse> {
  const { data } = await apiClient.post(`/v1/auth/invite/${token}/accept`, payload);
  return data;
}

// ---------------------------------------------------------------------------
// User lifecycle actions (CUSTOMER_ADMIN+)
// ---------------------------------------------------------------------------

/** Reactivate a previously deactivated user. */
export async function reactivateUser(userId: string): Promise<UserRecord> {
  const { data } = await apiClient.post<UserRecord>(`/v1/users/${userId}/reactivate`);
  return data;
}

/** Hard delete a user (irreversible). */
export async function hardDeleteUser(userId: string): Promise<void> {
  await apiClient.post(`/v1/users/${userId}/remove`);
}

/** Send a password reset email to a user. */
export async function sendPasswordReset(userId: string): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>(`/v1/users/${userId}/send-reset`);
  return data;
}

/** Admin sets a user's password directly (no email). */
export async function adminSetPassword(userId: string, newPassword: string): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>(`/v1/users/${userId}/set-password`, { new_password: newPassword });
  return data;
}

/** Update a user's role. */
export async function updateUserRole(userId: string, role: Role): Promise<UserRecord> {
  const { data } = await apiClient.put<UserRecord>(`/v1/users/${userId}`, { role });
  return data;
}

// ---------------------------------------------------------------------------
// Platform admin user management
// ---------------------------------------------------------------------------

/** List all users in a tenant (PLATFORM_ADMIN only). */
export async function adminListTenantUsers(tenantId: string): Promise<UserRecord[]> {
  const { data } = await apiClient.get<UserRecord[]>(`/admin/platform/tenants/${tenantId}/users`);
  return data;
}

/** Update a user's role or active status (PLATFORM_ADMIN only). */
export async function adminUpdateUser(userId: string, payload: { role?: Role; is_active?: boolean }): Promise<UserRecord> {
  const { data } = await apiClient.patch<UserRecord>(`/admin/platform/users/${userId}`, payload);
  return data;
}

/** Hard delete a user (PLATFORM_ADMIN only). */
export async function adminHardDeleteUser(userId: string): Promise<void> {
  await apiClient.delete(`/admin/platform/users/${userId}`);
}

/** Send password reset email (PLATFORM_ADMIN only). */
export async function adminSendPasswordReset(userId: string): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>(`/admin/platform/users/${userId}/send-reset`);
  return data;
}

/** Set a user's password directly (PLATFORM_ADMIN only). */
export async function adminSetPasswordPlatform(userId: string, newPassword: string): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>(`/admin/platform/users/${userId}/set-password`, { new_password: newPassword });
  return data;
}

// ---------------------------------------------------------------------------
// Self-service password reset (public — no auth)
// ---------------------------------------------------------------------------

/** Request a password reset email (always returns success, no user enumeration). */
export async function forgotPassword(email: string): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>('/v1/auth/forgot-password', { email });
  return data;
}

/** Validate a password reset token and return the associated email. */
export async function validateResetToken(token: string): Promise<{ email: string }> {
  const { data } = await apiClient.get<{ email: string }>(`/v1/auth/reset-password/${token}`);
  return data;
}

/** Reset a user's password using a valid token. */
export async function resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>(`/v1/auth/reset-password/${token}`, { new_password: newPassword });
  return data;
}
